/**
 * BottomPerformingNonASN
 *
 * "Bottom Performing Non-ASN" ranking table for the ASN Gap Overview tab.
 * Shows the (≤10) hierarchy rows (region/market/location, per the Overview
 * tab's Chart Configuration selection) with the highest raw Non-ASN load
 * counts — the biggest gap contributors.
 *
 * Data comes from `POST /api/asn/bottom-performing-non-asn` (fetched and
 * owned by AsnGapDashboard, passed down via Overview) — rank, footer copy
 * and over/under-target status are all provided by the backend rather than
 * computed client-side.
 */

import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { DLOSimpleTooltip } from '../../../../../modules/dlo/components/DLOTooltip';
import { asnOverviewChartsStyles as styles } from '../styles/asnOverviewChartsStyles';
import TableSkeleton from '../../../components/TableSkeleton';
import { formatCount, formatPercent2 } from '../utils/asnLoadsComparisonAdapter';
import { ASNBottomPerformingRow } from '../api/asnOverviewKpiApi';
import { FontFamily } from '../../../../../constants/fonts';

export interface BottomPerformingNonASNProps {
  hierarchyLabel?: string;
  /** Already-ranked (by the backend) rows from the API response's `rows` field. */
  rows: ASNBottomPerformingRow[];
  /** `response.footerMetadata.footerText` — rendered verbatim under the table. */
  footerText?: string;
  loading?: boolean;
  error?: string | null;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  onSortChange?: (column: string, direction: 'ASC' | 'DESC') => void;
  onRetry?: () => void;
  onRowPress?: (item: ASNBottomPerformingRow) => void;
  /** When true, renders a mobile card list instead of the desktop table. */
  isMobile?: boolean;
}

const BottomPerformingNonASN: React.FC<BottomPerformingNonASNProps> = ({
  hierarchyLabel = 'Region / Unit',
  rows,
  footerText,
  loading = false,
  error = null,
  sortColumn = 'NON_ASN_LOADS',
  sortDirection = 'DESC',
  onSortChange,
  onRetry,
  onRowPress,
  isMobile = false,
}) => {
  const handleHeaderPress = (col: string) => {
    if (!onSortChange) return;
    const isActive = sortColumn === col;
    const nextDirection: 'ASC' | 'DESC' = isActive
      ? sortDirection === 'DESC'
        ? 'ASC'
        : 'DESC'
      : 'DESC';
    onSortChange(col, nextDirection);
  };

  return (
    <View style={[styles.card, styles.stretchCard]} testID="bottom-performing-nonasn-card">
      <View style={styles.webCardHeaderSection}>
        {isMobile ? (
          /* ── Mobile header: title on first line; subtitle + info icon inline on second ── */
          <View style={styles.mobileHeaderRow}>
            <View style={styles.mobileHeaderLeft}>
              <Text style={styles.title} numberOfLines={1}>Bottom Performing Non-ASN</Text>
              <View style={styles.mobileSubtitleRow}>
                <Text style={styles.subTitle} numberOfLines={1}>{`by ${hierarchyLabel}`}</Text>
                <DLOSimpleTooltip
                  text="Shows the highest Non-ASN load counts first and identifies the biggest gap contributors at the current hierarchy level."
                  position="top"
                  trigger="click"
                >
                  <View style={styles.infoIconWrap}>
                    <MaterialIcons name="info-outline" size={16} color="#8A8F98" />
                  </View>
                </DLOSimpleTooltip>
              </View>
            </View>
          </View>
        ) : (
          /* ── Web/tablet header: existing single-row layout ── */
          <>
            <View style={styles.headerRow}>
              <View style={styles.headerTextGroup}>
                <Text style={styles.title}>Bottom Performing Non-ASN</Text>
                <Text style={styles.subTitle}>{`by ${hierarchyLabel}`}</Text>
              </View>
              <DLOSimpleTooltip
                text="Ranks hierarchy scopes by raw Non-ASN load count (highest first) to surface the biggest gap contributors at the current hierarchy level."
                position="top"
                trigger="hover"
              >
                <View style={styles.infoIconWrap}>
                  <MaterialIcons name="info-outline" size={16} color="#8A8F98" />
                </View>
              </DLOSimpleTooltip>
            </View>
            <Text style={styles.description}>
              Highest Non-ASN load counts first. Identifies the biggest gap contributors at the current hierarchy level.
            </Text>
          </>
        )}
      </View>

      {loading ? (
        <TableSkeleton rows={10} />
      ) : error ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.errorText}>{error}</Text>
          {onRetry && (
            <Pressable style={styles.retryButton} onPress={onRetry} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.retryButtonText}>Retry</Text>
            </Pressable>
          )}
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No load data available for the selected filters</Text>
        </View>
      ) : isMobile ? (
        // ── Mobile card list ────────────────────────────────────────────────
        <View style={mobileStyles.cardList}>
          {rows.map((row, index) => {
            const overTarget = row.status === 'OFF_TARGET';
            const isLast = index === rows.length - 1;
            const isDrillable = !!onRowPress;
            const rowKey = `${row.rank}-${row.label}`;

            const pctColor = overTarget ? '#C12C01' : '#0B5FC6';

            const cardContent = (
              <View style={[mobileStyles.card, isLast && mobileStyles.cardLast]}>
                {/* Row 1: rank badge + label */}
                <View style={mobileStyles.cardTopRow}>
                  <View style={mobileStyles.rankBadge}>
                    <Text style={mobileStyles.rankBadgeText}>{row.rank}</Text>
                  </View>
                  <Text style={mobileStyles.cardLabel} numberOfLines={2}>
                    {row.label}
                  </Text>
                </View>

                {/* Row 2: three metric boxes — equal width, full card width */}
                <View style={mobileStyles.cardBottomRow}>
                  <View style={mobileStyles.metricBox}>
                    <Text style={mobileStyles.metricLabel} numberOfLines={1}>NON-ASN</Text>
                    <Text style={mobileStyles.metricValue} numberOfLines={1}>
                      {formatCount(row.nonAsnLoads)}
                    </Text>
                  </View>
                  <View style={mobileStyles.metricBox}>
                    <Text style={mobileStyles.metricLabel} numberOfLines={1}>TOTAL</Text>
                    <Text style={mobileStyles.metricValue} numberOfLines={1}>
                      {formatCount(row.totalLoads)}
                    </Text>
                  </View>
                  <View style={mobileStyles.metricBox}>
                    <Text style={mobileStyles.metricLabel} numberOfLines={1}>% NON-ASN</Text>
                    <Text style={[mobileStyles.metricValue, { color: pctColor }]} numberOfLines={1}>
                      {formatPercent2(row.percentNonAsn)}
                    </Text>
                  </View>
                </View>
              </View>
            );

            if (!isDrillable) {
              return (
                <View key={rowKey} testID={`bottom-performing-row-${row.rank}`}>
                  {cardContent}
                </View>
              );
            }

            return (
              <Pressable
                key={rowKey}
                onPress={() => onRowPress?.(row)}
                style={({ pressed }) => [pressed && { opacity: 0.7 }]}
                accessibilityRole="button"
                accessibilityLabel={`${row.label}: rank ${row.rank}, ${formatCount(
                  row.nonAsnLoads,
                )} non-ASN loads of ${formatCount(row.totalLoads)} total, ${formatPercent2(
                  row.percentNonAsn,
                )} non-ASN. Activate to view details.`}
                testID={`bottom-performing-row-${row.rank}`}
              >
                {cardContent}
              </Pressable>
            );
          })}

          {!!footerText && (
            <Text style={styles.tableFooterText}>{footerText}</Text>
          )}
        </View>
      ) : (
        // ── Desktop / tablet table ──────────────────────────────────────────
        <View style={styles.tableWrap}>
          <View style={styles.tableHeaderRow}>
            <View style={styles.tableHeaderCellRank} />
            <Pressable
              style={styles.tableHeaderCell}
              onPress={() => handleHeaderPress('LABEL')}
              accessibilityRole="button"
              accessibilityLabel={`Sort by ${hierarchyLabel}`}
              testID="bottom-performing-sort-label"
            >
              <Text style={styles.tableHeaderText}>{hierarchyLabel.toUpperCase()}</Text>
              <MaterialIcons
                name={sortColumn === 'LABEL' ? (sortDirection === 'ASC' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                size={14}
                color={sortColumn === 'LABEL' ? '#0B5FC6' : '#9AA1AB'}
              />
            </Pressable>
            <Pressable
              style={styles.tableHeaderCell}
              onPress={() => handleHeaderPress('NON_ASN_LOADS')}
              accessibilityRole="button"
              accessibilityLabel="Sort by Non-ASN Loads"
              testID="bottom-performing-sort-non-asn-loads"
            >
              <Text style={styles.tableHeaderText}>NON-ASN LOADS</Text>
              <MaterialIcons
                name={sortColumn === 'NON_ASN_LOADS' ? (sortDirection === 'ASC' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                size={14}
                color={sortColumn === 'NON_ASN_LOADS' ? '#0B5FC6' : '#9AA1AB'}
              />
            </Pressable>
            <Pressable
              style={styles.tableHeaderCell}
              onPress={() => handleHeaderPress('TOTAL_LOADS')}
              accessibilityRole="button"
              accessibilityLabel="Sort by Total Loads"
              testID="bottom-performing-sort-total-loads"
            >
              <Text style={styles.tableHeaderText}>TOTAL LOADS</Text>
              <MaterialIcons
                name={sortColumn === 'TOTAL_LOADS' ? (sortDirection === 'ASC' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                size={14}
                color={sortColumn === 'TOTAL_LOADS' ? '#0B5FC6' : '#9AA1AB'}
              />
            </Pressable>
            <Pressable
              style={styles.tableHeaderCell}
              onPress={() => handleHeaderPress('PERCENT_NON_ASN')}
              accessibilityRole="button"
              accessibilityLabel="Sort by Percent Non-ASN Loads"
              testID="bottom-performing-sort-percent-non-asn"
            >
              <Text style={styles.tableHeaderText}>% NON-ASN LOADS</Text>
              <MaterialIcons
                name={sortColumn === 'PERCENT_NON_ASN' ? (sortDirection === 'ASC' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                size={14}
                color={sortColumn === 'PERCENT_NON_ASN' ? '#0B5FC6' : '#9AA1AB'}
              />
            </Pressable>
          </View>

          {rows.map((row, index) => {
            const overTarget = row.status === 'OFF_TARGET';
            const isLast = index === rows.length - 1;
            const isDrillable = !!onRowPress;
            const rowKey = `${row.rank}-${row.label}`;

            const rowContent = (
              <>
                <View style={styles.tableHeaderCellRank}>
                  <View style={styles.rankBadge}>
                    <Text style={styles.rankBadgeText}>{row.rank}</Text>
                  </View>
                </View>
                <View style={styles.tableCell}>
                  <Text style={styles.tableCellLabel} numberOfLines={1}>
                    {row.label}
                  </Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={[styles.tableCellValue, { textAlign: 'left' }]}>{formatCount(row.nonAsnLoads)}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={[styles.tableCellValue, { textAlign: 'left' }]}>{formatCount(row.totalLoads)}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={[styles.tableCellPct, overTarget ? styles.pctRed : styles.pctBlue, { textAlign: 'left' }]}>
                    {formatPercent2(row.percentNonAsn)}
                  </Text>
                </View>
              </>
            );

            const rowStyle = [styles.tableRow, isLast && styles.tableRowLast];

            if (!isDrillable) {
              return (
                <View key={rowKey} style={[...rowStyle, { cursor: 'default' } as any]} testID={`bottom-performing-row-${row.rank}`}>
                  {rowContent}
                </View>
              );
            }

            return (
              <Pressable
                key={rowKey}
                onPress={() => onRowPress?.(row)}
                style={({ pressed }) => [...rowStyle, { cursor: 'pointer' } as any, pressed && { opacity: 0.7 }]}
                accessibilityRole="button"
                accessibilityLabel={`${row.label}: rank ${row.rank}, ${formatCount(
                  row.nonAsnLoads,
                )} non-ASN loads of ${formatCount(row.totalLoads)} total, ${formatPercent2(
                  row.percentNonAsn,
                )} non-ASN. Activate to view details.`}
                testID={`bottom-performing-row-${row.rank}`}
              >
                {rowContent}
              </Pressable>
            );
          })}

          <Text style={styles.tableFooterText}>{footerText}</Text>
        </View>
      )}
    </View>
  );
};

// ─── Mobile-only card styles ──────────────────────────────────────────────────

const mobileStyles = StyleSheet.create({
  cardList: {
    width: '100%',
    gap: 10,
  },
  // Card is now a vertical column so the two rows stack naturally.
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    flexDirection: 'column',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    rowGap: 10,
  },
  cardLast: {
    // no extra treatment needed — gap handles spacing
  },
  // Row 1: rank badge + label side-by-side; label takes remaining width.
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 8,
  },
  rankBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FDEDEA',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  rankBadgeText: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#C12C01',
  },
  cardLabel: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.medium,
    fontWeight: '700',
    color: '#32363F',
    lineHeight: 22,
  },
  // Row 2: three metric boxes — equal width, full card width, evenly spaced.
  cardBottomRow: {
    flexDirection: 'row',
    columnGap: 8,
  },
  // flex: 1 makes all three boxes share the row width equally.
  metricBox: {
    flex: 1,
    backgroundColor: '#F5F7FA',
    borderRadius: 12,
    height: 56,
    paddingHorizontal: 6,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  metricLabel: {
    fontSize: 10,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#7A7F87',
    lineHeight: 12,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  metricValue: {
    marginTop: 3,
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '800',
    lineHeight: 16,
    color: '#2F2F2F',
    textAlign: 'center',
  },
});

export default BottomPerformingNonASN;
